import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { BookOpen, ArrowLeft, Mail, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Using XMLHttpRequest to avoid "request object cannot be cloned" error
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/auth/forgot-password`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.timeout = 30000;
    
    let handled = false;
    
    const handleSuccess = () => {
      if (handled) return;
      handled = true;
      setLoading(false);
      setSubmitted(true);
      toast.success('Check your email for the reset link');
    };
    
    const handleError = (message) => {
      if (handled) return;
      handled = true;
      setLoading(false);
      toast.error(message);
    };
    
    xhr.onreadystatechange = function() {
      if (xhr.readyState === 4 && !handled) {
        try {
          if (xhr.status >= 200 && xhr.status < 300) {
            handleSuccess();
          } else if (xhr.status > 0) {
            const data = JSON.parse(xhr.responseText);
            handleError(data.detail || 'Failed to send reset email');
          }
        } catch (err) {
          if (xhr.status >= 200 && xhr.status < 300) {
            handleSuccess();
          }
        }
      }
    };
    
    xhr.onerror = function() {
      handleError('Network error. Please check your connection and try again.');
    };
    
    xhr.ontimeout = function() {
      handleError('Request timed out. Please try again.');
    };
    
    xhr.send(JSON.stringify({ email }));
  };

  return (
    <div className="min-h-screen flex">
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1639753759816-fd5442a41d1f?q=85&w=800&auto=format&fit=crop')` }}
      >
        <div className="absolute inset-0 bg-[#3A5A40]/40"></div>
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif' }}>Reset Your Password</h1>
          <p className="text-xl leading-relaxed">We'll help you get back into your account.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#FAEDCD]">
        <Card className="w-full max-w-md p-8 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-lg rounded-2xl">
          <div className="flex items-center justify-center mb-6">
            <BookOpen className="w-12 h-12 text-[#3A5A40]" strokeWidth={1.5} />
          </div>
          
          {!submitted ? (
            <>
              <h2 className="text-3xl font-bold text-center mb-2 text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
                Forgot Password?
              </h2>
              <p className="text-center text-[#588157] mb-6">
                Enter your email and we'll send you a link to reset your password.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-[#344E41]">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="Enter your email"
                    data-testid="forgot-email-input"
                    className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg placeholder:text-[#A3B18A]"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-submit-button"
                  className="w-full bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md duration-300 hover:shadow-lg active:scale-95 font-medium rounded-lg"
                >
                  {loading ? (
                    'Sending...'
                  ) : (
                    <>
                      <Mail className="w-5 h-5 mr-2" strokeWidth={1.5} />
                      Send Reset Link
                    </>
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-[#3A5A40]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-[#3A5A40]" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl font-bold text-[#344E41] mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
                Check Your Email
              </h2>
              <p className="text-[#588157] mb-6">
                If an account exists with <strong>{email}</strong>, you will receive a password reset link shortly.
              </p>
              <p className="text-sm text-[#A3B18A]">
                Didn't receive the email? Check your spam folder or try again.
              </p>
              <Button
                onClick={() => setSubmitted(false)}
                variant="outline"
                className="mt-4 border-[#3A5A40] text-[#3A5A40] hover:bg-[#3A5A40] hover:text-white"
                data-testid="try-again-button"
              >
                Try Again
              </Button>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="inline-flex items-center text-[#3A5A40] font-medium hover:underline"
              data-testid="back-to-login-link"
            >
              <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Back to Login
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
